import { useMemo } from 'react';
import {
  AlertCircle,
  BadgeCheck,
  Ban,
  Check,
  ClipboardList,
  Clock,
  CreditCard,
  PackageCheck,
  Truck,
  Undo2,
} from 'lucide-react';
import { formatNumber } from '@/features/reports/lib/format';
import { formatJalaliDate } from '@/lib/format';
import { buildOrderTimeline } from '../lib/orderTimeline';
import '../orderTimeline.css';

const STEP_ICONS = {
  placed: ClipboardList,
  paid: CreditCard,
  accepted: BadgeCheck,
  shipped: Truck,
  delivered: PackageCheck,
};

const STAGE_ICONS = {
  awaiting_payment: CreditCard,
  cheque_registration: Clock,
  cheque_confirmation: Clock,
  awaiting_seller: Clock,
  seller_processing: BadgeCheck,
  preparing: BadgeCheck,
  partially_shipped: Truck,
  shipped: Truck,
  delivered: PackageCheck,
  rejected: Ban,
  cancelled: Ban,
  refund_due: Undo2,
  failed: AlertCircle,
};

const STATE_TEXT = {
  done: 'انجام شد',
  current: 'مرحلهٔ فعلی',
  pending: 'در انتظار',
  stopped: 'متوقف شد',
};

const useTimeline = (order, role) => useMemo(
  () => buildOrderTimeline(order, { role }),
  [order, role],
);

export function OrderStatusBadge({ order, role = 'customer' }) {
  const timeline = useTimeline(order, role);
  const Icon = STAGE_ICONS[timeline.stage] ?? Clock;
  return (
    <span className="ost-badge" data-tone={timeline.tone} title={timeline.hint}>
      <Icon aria-hidden="true" size={14} />
      {timeline.label}
    </span>
  );
}

export function OrderMiniProgress({ order, role = 'customer' }) {
  const timeline = useTimeline(order, role);
  const done = timeline.steps.filter((step) => step.state === 'done').length;
  return (
    <span
      aria-label={`${formatNumber(done)} از ${formatNumber(timeline.steps.length)} مرحله انجام شده`}
      className="ost-mini"
      role="img"
    >
      {timeline.steps.map((step) => (
        <span className={`ost-mini-dot is-${step.state}`} key={step.key} />
      ))}
    </span>
  );
}

export default function OrderStatusTimeline({ order, role = 'customer', variant = 'full' }) {
  const timeline = useTimeline(order, role);
  const compact = variant === 'compact';
  const HeadlineIcon = STAGE_ICONS[timeline.stage] ?? Clock;
  const BranchIcon = STAGE_ICONS[timeline.branch?.key] ?? Ban;

  return (
    <section
      aria-label="روند سفارش"
      className={`ost${compact ? ' ost--compact' : ''}`}
      data-tone={timeline.tone}
    >
      {compact ? (
        <p className="ost-hint">{timeline.hint}</p>
      ) : (
        <header className="ost-headline">
          <span aria-hidden="true" className="ost-headline-icon">
            <HeadlineIcon size={20} />
          </span>
          <div>
            <strong>{timeline.label}</strong>
            <p>{timeline.hint}</p>
          </div>
        </header>
      )}

      <ol className="ost-steps">
        {timeline.steps.map((step) => {
          const Icon = STEP_ICONS[step.key];
          return (
            <li
              aria-current={step.state === 'current' ? 'step' : undefined}
              className={`ost-step is-${step.state}`}
              key={step.key}
            >
              <span aria-hidden="true" className="ost-node">
                {step.state === 'done' ? <Check size={compact ? 14 : 18} /> : <Icon size={compact ? 13 : 16} />}
              </span>
              <span className="ost-label">{step.label}</span>
              {step.date && !compact && (
                <span className="ost-date">{formatJalaliDate(step.date, { includeTime: true })}</span>
              )}
              {step.detail && <span className="ost-detail">{step.detail}</span>}
              <span className="ost-sr">{STATE_TEXT[step.state]}</span>
            </li>
          );
        })}
      </ol>

      {timeline.branch && !compact && (
        <div className="ost-branch" data-tone={timeline.branch.tone} role="status">
          <BranchIcon aria-hidden="true" size={16} />
          مسیر سفارش متوقف شد: {timeline.branch.label}
        </div>
      )}

      {timeline.notes.map((note) => (
        <p className="ost-note" data-tone={note.tone} key={note.key}>{note.text}</p>
      ))}

      {!compact && !timeline.itemsKnown && (
        <p className="ost-footnote">
          وضعیت دقیق هر کالا در این نما موجود نیست؛ مرحله‌ها بر اساس وضعیت کلی سفارش نمایش داده شده‌اند.
        </p>
      )}
    </section>
  );
}